import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { FsPermission } from 'libs/api/entities/src/lib/permission';
import { FsPermissionsAPI } from 'libs/api/services/src/lib/permissions.service';
import { ISnListQueryParameters } from 'libs/api/src/lib/interfaces/list-query-parameters.interface';
import { ISnListResponse } from 'libs/api/src/lib/interfaces/list-response.interface';
import { SnDataListComponent } from 'libs/ui/data-list/src/lib/components';

@Component({
  selector: 'fs-permissions-search-dialog',
  templateUrl: './permissions-search-dialog.component.html',
  styleUrls: ['./permissions-search-dialog.component.scss'],
})
export class PermissionsSearchDialogComponent implements OnDestroy, OnInit {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<FsPermission>;
  public readonly loadData = this._loadData.bind(this);
  public readonly columns: string[] = ['select', 'object', 'action', 'label'];

  public selection = new SelectionModel<FsPermission>(true, []);
  public selected;
  public pageSize;
  public itemChecked;

  private _fullData: FsPermission[];

  private readonly _destroy$ = new Subject();

  constructor(
    private readonly _dialogRef: MatDialogRef<PermissionsSearchDialogComponent>,
    private readonly _permissionsApi: FsPermissionsAPI,
  ) {}

  public ngOnInit(): void {
    this.selected = '20';
    this.selection.changed.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.itemChecked = this.selection.hasValue();
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSearchValueChange(value: string): void {
    const data = this._fullData;
    this.list.dataSource.clear();
    if (value) {
      this.list.dataSource.insertItem(
        ...data.filter((v) => {
          const search = value.toLowerCase();
          const name = v.name.toLowerCase();
          const code = v.codename.toLowerCase();
          const appLabel = v.appLabel.toLowerCase();
          return name.includes(search) || code.includes(search) || appLabel.includes(search);
        }),
      );
    } else {
      this.list.dataSource.insertItem(...data);
    }
  }

  public submitForm(): void {
    this._reloadData();
  }

  public onSelected(layer: any): void {
    this._dialogRef.close(layer);
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.list.dataSource.data.length;
    return numSelected === numRows;
  }

  public savePermissions(): void {
    const permissions = this.selection.selected.map((permission) => permission);
    this.onSelected(permissions);
  }

  public masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.list.dataSource.data.forEach((row) => this.selection.select(row));
  }

  public loadAllLocations(): void {
    this.pageSize = 'all';
    this._reloadData();
  }

  public loadTenLocations(): void {
    this.pageSize = 10;
    this._reloadData();
  }

  public loadTwentyLocations(): void {
    this.pageSize = 20;
    this._reloadData();
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<FsPermission>> {
    return this._permissionsApi.getPermissionsList(this._prepareQueryParams(params)).pipe(
      tap((res) => {
        this._fullData = res.results;
      }),
      map((res) => {
        res.results.sort((a, b) => a.appLabel.localeCompare(b.appLabel));
        return res;
      }),
      takeUntil(this._destroy$),
    );
  }

  private _prepareQueryParams(params: any): any {
    return {
      ordering: {
        active: 'name',
        direction: 'asc',
      },
      page: params.page,
      pageSize: 'all',
    };
  }
}
